import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Download, FileText, ShieldCheck, TrendingUp, Radio, Leaf, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';
import { SmsOptInCheckbox } from '@/components/SmsConsentDisclosure';

const GOLD = '#BCA480';
const DARK = '#434345';
const PDF_PATH = '/downloads/bespoke-property-management-summary.pdf';

// -----------------------------------------------------------------------------
// Small presentational atoms
// -----------------------------------------------------------------------------

const Eyebrow = ({ children }) => (
  <div
    className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-semibold"
    style={{ color: GOLD }}
  >
    <span className="w-6 h-px" style={{ background: GOLD }} />
    {children}
  </div>
);

const SectionHeading = ({ eyebrow, title, kicker, dark = false }) => (
  <div className="mb-10 max-w-3xl">
    {eyebrow && (
      <div className="mb-4">
        <Eyebrow>{eyebrow}</Eyebrow>
      </div>
    )}
    <h2
      className={`text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight ${
        dark ? 'text-white' : ''
      }`}
      style={dark ? {} : { color: DARK }}
    >
      {title}
    </h2>
    {kicker && (
      <p className={`mt-4 text-base sm:text-lg leading-relaxed ${dark ? 'text-white/75' : 'text-gray-600'}`}>
        {kicker}
      </p>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Email-gated Summary download modal
// -----------------------------------------------------------------------------

const SummaryDownloadDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', sms_opt_in: false });
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post('/leads/pm-summary-download', form);
      setDownloadUrl(data.download_url || PDF_PATH);
      toast.success("Your download is ready — we've also emailed you a copy.");
    } catch (err) {
      toast.error("We couldn't process your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDownloadUrl(null);
    setForm({ name: '', email: '', phone: '', sms_opt_in: false });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="pm-summary-dialog">
        {!downloadUrl ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ color: DARK }}>
                Download the Summary of Services
              </DialogTitle>
              <DialogDescription className="pt-1">
                A concise 4-page PDF covering how we manage a property as an investment — the four pillars,
                our real differentiators, guarantees, and transparent pricing.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="pm-dl-name">Full Name *</Label>
                <Input
                  id="pm-dl-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="pm-summary-name-input"
                />
              </div>
              <div>
                <Label htmlFor="pm-dl-email">Email *</Label>
                <Input
                  id="pm-dl-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="pm-summary-email-input"
                />
              </div>
              <div>
                <Label htmlFor="pm-dl-phone">Phone (optional)</Label>
                <Input
                  id="pm-dl-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="pm-summary-phone-input"
                />
                <SmsOptInCheckbox
                  checked={form.sms_opt_in}
                  onChange={(v) => setForm({ ...form, sms_opt_in: v })}
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full text-white"
                style={{ backgroundColor: GOLD }}
                data-testid="pm-summary-submit-btn"
              >
                {submitting ? 'Preparing your download…' : 'Send Me the Summary'}
              </Button>
              <p className="text-[11px] text-gray-500 leading-snug">
                No spam. We'll email the PDF and occasionally follow up with owner-focused notes.
                Unsubscribe anytime.
              </p>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="pm-summary-success">
            <div
              className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4"
              style={{ backgroundColor: `${GOLD}20` }}
            >
              <Check className="h-7 w-7" style={{ color: GOLD }} />
            </div>
            <h3 className="text-2xl font-bold" style={{ color: DARK }}>
              Your summary is ready.
            </h3>
            <p className="text-sm text-gray-600 mt-2 mb-6">
              We've also emailed a copy to <strong>{form.email}</strong>.
            </p>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-md font-semibold w-full"
              style={{ backgroundColor: GOLD }}
              data-testid="pm-summary-download-link"
            >
              <Download className="h-4 w-4" /> Download the PDF
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------

const PropertyManagement = () => {
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_address: '',
    property_type: '',
    message: '',
    department: 'property_management',
    sms_opt_in: false,
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contact', formData);
      toast.success("Your inquiry has been submitted! We'll be in touch shortly.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        property_address: '',
        property_type: '',
        message: '',
        department: 'property_management',
        sms_opt_in: false,
      });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    }
  };

  const pillars = [
    {
      icon: ShieldCheck,
      title: 'Protect',
      body: 'The property, the owner, and the long-term condition of the investment.',
    },
    {
      icon: TrendingUp,
      title: 'Perform',
      body: 'Continuously evaluate rent, vacancy, expenses, and improvement opportunities.',
    },
    {
      icon: Radio,
      title: 'Communicate',
      body: 'Keep owners informed. Make the management experience transparent and predictable.',
    },
    {
      icon: Leaf,
      title: 'Preserve',
      body: 'Treat maintenance, tenant relationships, and documentation as investment stewardship.',
    },
  ];

  const differentiators = [
    {
      title: 'Actual Property Inspections',
      body: 'Quarterly drive-by monitoring plus real biannual walkthroughs — not just paperwork.',
    },
    {
      title: 'No Repair Markups',
      body: 'We do not up-charge repairs. Company vendor discounts are passed along to the owner.',
    },
    {
      title: 'Thorough Property Documentation',
      body: 'Condition is documented and expectations set at move-in — a clear record for the life of the tenancy.',
    },
    {
      title: 'Proactive Lease Protection',
      body: 'Our lease addenda address bedbugs, repeated late payments, abandonment, and other issues before they escalate.',
    },
    {
      title: 'Market-Driven Rent Strategy',
      body: 'We continually evaluate the market and rental pricing rather than setting the rent once and forgetting.',
    },
    {
      title: 'Detailed Financial Reporting',
      body: 'Monthly income and expense reporting, year-end reporting, and 1099 documentation prepared for your records.',
    },
    {
      title: 'Owner Consultation',
      body: 'Direct updates on property performance, market conditions, improvements, and investment opportunities.',
    },
  ];

  const approachSteps = [
    { n: '01', title: 'Evaluate', body: 'Understand the property, condition, market, rent potential, and owner objectives.' },
    { n: '02', title: 'Prepare', body: 'Position the property for the market and set expectations before the tenant moves in.' },
    { n: '03', title: 'Place', body: 'Market the property and conduct a thorough tenant screening and approval process.' },
    { n: '04', title: 'Protect', body: 'Monitor the property, manage maintenance, document condition, and enforce the lease.' },
    { n: '05', title: 'Optimize', body: 'Continually evaluate rent, vacancy, expenses, and opportunities to improve the investment.' },
  ];

  const experienceItems = [
    { headline: 'Less chasing.', body: 'We handle tenant communication and the day-to-day.' },
    { headline: 'Less guessing.', body: 'You receive real reporting and proactive updates.' },
    { headline: 'Fewer surprises.', body: 'Inspections and proactive oversight surface issues earlier.' },
    { headline: 'More confidence.', body: 'The property is actively monitored, not managed from behind a desk.' },
    { headline: 'More strategic thinking.', body: "We don't just maintain the status quo — we continually consider rent, expenses, condition, and opportunities to improve the investment." },
  ];

  return (
    <div className="min-h-screen bg-[#f9f7f3]" data-testid="property-management-page">
      <SEO
        title="Property Management in Memphis, TN & North Mississippi | Bespoke Realty"
        description="Investment-focused property management in Memphis, Southaven, Olive Branch, Hernando, and DeSoto County. Biannual walkthroughs, no repair markups, market-driven rent strategy, and a 30-day exit guarantee. Download our full Summary of Services."
        keywords="property management Memphis, rental property manager Tennessee, property management Mississippi, landlord services Memphis, tenant screening Southaven, property management Olive Branch, DeSoto County rental management, investor property management TN MS"
      />
      <StructuredData schema={[
        schemas.service({
          name: 'Residential Property Management',
          serviceType: 'PropertyManagement',
          description: 'Investment-focused property management for single-family and small multi-family rentals across Memphis, DeSoto County, and surrounding areas. Biannual walkthroughs, market-driven rent reviews, no repair markups, and transparent monthly owner statements.',
        }),
        schemas.breadcrumb([{ name: 'Home', url: '/' }, { name: 'Property Management', url: '/property-management' }]),
      ]} />

      <SummaryDownloadDialog open={downloadOpen} onOpenChange={setDownloadOpen} />

      {/* ---------- HERO ---------- */}
      <section className="relative h-[72vh] min-h-[540px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40 z-10" />
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/5flhmb23_PropertyManagement.png)',
          }}
        />
        <div className="relative z-20 w-full max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="mb-5">
            <span
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm"
              style={{ background: `${GOLD}25`, color: '#f6ecd6', border: `1px solid ${GOLD}55` }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
              For Property Owners &amp; Investors
            </span>
          </div>
          <h1
            className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
            style={{ color: '#ffffff' }}
            data-testid="pm-heading"
          >
            <span className="text-white" style={{ color: '#ffffff' }}>Property Management<br />That </span>
            <span style={{ color: GOLD }}>Protects the Investment.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto mb-8">
            Owning a rental property should build wealth, not consume your time. Bespoke Realty manages
            the details, protects the property, and continually looks for ways to improve its
            performance.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#owner-inquiry"
              className="inline-flex items-center justify-center gap-2 text-white px-6 py-3.5 rounded-md font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: GOLD }}
              data-testid="pm-hero-cta-consult"
            >
              Request a Management Consultation <ArrowRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => setDownloadOpen(true)}
              className="inline-flex items-center justify-center gap-2 border border-white/50 text-white hover:bg-white/10 px-6 py-3.5 rounded-md font-semibold transition-colors"
              data-testid="pm-hero-cta-download"
            >
              <Download className="h-4 w-4" /> Download Summary of Services
            </button>
          </div>
        </div>
      </section>

      {/* ---------- TRUST BAR ---------- */}
      <section className="text-white py-8" style={{ backgroundColor: DARK }} data-testid="pm-stats-bar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold" style={{ color: GOLD }}>TN &amp; MS</div>
            <div className="text-xs sm:text-sm text-white/70 mt-1">Licensed in both states</div>
          </div>
          <div>
            <div className="text-3xl font-bold" style={{ color: GOLD }}>Biannual</div>
            <div className="text-xs sm:text-sm text-white/70 mt-1">Walkthroughs, not just drive-bys</div>
          </div>
          <div>
            <div className="text-3xl font-bold" style={{ color: GOLD }}>0%</div>
            <div className="text-xs sm:text-sm text-white/70 mt-1">Markup on repairs</div>
          </div>
          <div>
            <div className="text-3xl font-bold" style={{ color: GOLD }}>30-Day</div>
            <div className="text-xs sm:text-sm text-white/70 mt-1">Exit — no charge, no penalty</div>
          </div>
        </div>
      </section>

      {/* ---------- OWNER FIRST ---------- */}
      <section className="py-24" data-testid="pm-owner-first">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>Owner First</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" style={{ color: DARK }}>
              You own the investment.<br />
              <span style={{ color: GOLD }}>We manage the details.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-2">
            <p className="text-lg leading-relaxed text-gray-700 mb-5">
              Your rental property is an investment. It deserves more than someone who collects the rent
              and waits for something to go wrong.
            </p>
            <p className="text-lg leading-relaxed text-gray-700">
              Our approach combines tenant management, property oversight, maintenance coordination,
              financial reporting, market analysis, and proactive communication — protecting the property
              while pursuing its best possible performance.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- FOUR PILLARS ---------- */}
      <section className="py-24 border-y border-[#e6dec7]/60" style={{ backgroundColor: '#f4efe6' }} data-testid="pm-pillars">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="The Bespoke Approach"
            title="Four pillars behind every property we manage."
            kicker="Every management decision runs through these four lenses. Together, they turn day-to-day rental administration into long-term investment stewardship."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#e6dec7]/60 rounded-lg overflow-hidden">
            {pillars.map((p) => (
              <div key={p.title} className="bg-[#faf8f4] p-8 group hover:bg-white transition-colors" data-testid={`pm-pillar-${p.title.toLowerCase()}`}>
                <p.icon className="h-8 w-8 mb-5" style={{ color: GOLD }} strokeWidth={1.5} />
                <div className="text-xs tracking-[0.2em] uppercase font-semibold mb-2" style={{ color: GOLD }}>
                  {p.title}
                </div>
                <p className="text-[15px] leading-relaxed" style={{ color: DARK }}>
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHAT MAKES BESPOKE DIFFERENT ---------- */}
      <section className="py-24 bg-white" data-testid="pm-differentiators">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="What Makes Bespoke Different"
            title="Proof, not promises."
            kicker="Every property manager will tell you they collect rent, place tenants, and handle maintenance. These are the concrete practices that separate our approach."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {differentiators.map((d, i) => (
              <div key={d.title} className="flex gap-5" data-testid={`pm-diff-${i}`}>
                <div className="flex-shrink-0 pt-1">
                  <div className="text-[11px] font-bold tracking-widest" style={{ color: GOLD }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: DARK }}>{d.title}</h3>
                  <p className="text-[15px] leading-relaxed text-gray-600">{d.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- 5-STEP APPROACH ---------- */}
      <section className="py-24" style={{ backgroundColor: DARK }} data-testid="pm-approach">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Our Process"
            title="How we manage a property, end to end."
            kicker="A repeatable process that keeps a property protected today and performing tomorrow."
            dark
          />
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {approachSteps.map((s, i) => (
              <div key={s.n} className="relative border-t-2 pt-6" style={{ borderColor: GOLD }} data-testid={`pm-step-${s.n}`}>
                <div className="text-xs font-bold tracking-[0.22em]" style={{ color: GOLD }}>{s.n}</div>
                <h3 className="text-xl font-bold text-white mt-2 mb-2">{s.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ON-PAGE SUMMARY OF SERVICES + DOWNLOAD ---------- */}
      <section className="py-24 bg-white" data-testid="pm-summary-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <Eyebrow>Summary of Services</Eyebrow>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" style={{ color: DARK }}>
                What we actually do.
              </h2>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-600 max-w-2xl">
                Full transparency, on this page. The complete 4-page PDF walks through the same content —
                philosophy, differentiators, guarantees, and pricing — and is yours to keep.
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                {[
                  {
                    heading: 'Tenant Management',
                    items: [
                      'Tenant screening & selection',
                      'Lease preparation & renewals',
                      'Rent collection & processing',
                      'Communication & conflict resolution',
                      'Notices, enforcement & eviction coordination',
                    ],
                  },
                  {
                    heading: 'Property Management',
                    items: [
                      'Quarterly drive-by monitoring',
                      'Biannual walkthrough inspections',
                      'Maintenance & emergency response',
                      'Property condition documentation',
                      'Vendor coordination & preventative oversight',
                    ],
                  },
                  {
                    heading: 'Financial Management',
                    items: [
                      'Monthly income & expense reporting',
                      'Year-end reporting & 1099 preparation',
                      'Security deposit accounting',
                    ],
                  },
                  {
                    heading: 'Investment Oversight',
                    items: [
                      'Rental market analysis & pricing strategy',
                      'Vacancy reduction',
                      'Improvement & expense review',
                      'Property performance discussions',
                    ],
                  },
                ].map((g) => (
                  <div key={g.heading} data-testid={`pm-services-${g.heading.toLowerCase().replace(/\s+/g, '-')}`}>
                    <h3 className="text-xs tracking-[0.2em] uppercase font-bold mb-3" style={{ color: GOLD }}>
                      {g.heading}
                    </h3>
                    <ul className="space-y-2">
                      {g.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-[15px] text-gray-700 leading-snug">
                          <Check className="h-4 w-4 flex-shrink-0 mt-1" style={{ color: GOLD }} />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div
                className="rounded-lg p-8 shadow-lg border border-[#e6dec7]"
                style={{ backgroundColor: '#faf8f4' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: GOLD }}>
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: GOLD }}>
                      Free · 4-page PDF
                    </div>
                    <div className="text-lg font-bold" style={{ color: DARK }}>Take it with you.</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">
                  A printable, shareable copy of Bespoke Realty's Property Management Summary of Services —
                  ideal to review with a spouse, an accountant, or your business partner before we talk.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-gray-700">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4" style={{ color: GOLD }} /> The four management pillars</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4" style={{ color: GOLD }} /> Real differentiators & guarantees</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4" style={{ color: GOLD }} /> Transparent pricing</li>
                </ul>
                <button
                  type="button"
                  onClick={() => setDownloadOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-2 text-white px-5 py-3.5 rounded-md font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: GOLD }}
                  data-testid="pm-summary-download-cta"
                >
                  <Download className="h-4 w-4" /> Download the Summary
                </button>
                <p className="text-[11px] text-gray-500 mt-3 text-center">
                  Email required. We'll send you a copy for your records.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TRANSPARENT PRICING ---------- */}
      <section className="py-24" style={{ backgroundColor: '#f4efe6' }} data-testid="pm-pricing-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Transparent Pricing"
            title="No hidden fees. No surprises."
            kicker="Straightforward property management pricing you can plan around."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            <div className="bg-white rounded-lg p-8 border border-[#e6dec7]">
              <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Monthly Management</div>
              <div className="text-5xl font-bold" style={{ color: DARK }}>8–12%</div>
              <div className="text-sm text-gray-600 mt-1 mb-6">of collected rent</div>
              <p className="text-sm text-gray-600 leading-relaxed">
                All-inclusive management fee. Only charged when your property is producing income.
              </p>
            </div>
            <div className="rounded-lg p-8 relative shadow-lg border-2" style={{ borderColor: GOLD, backgroundColor: '#faf8f4' }}>
              <div className="absolute -top-3 left-8 text-[10px] tracking-[0.2em] uppercase font-bold text-white px-3 py-1 rounded-full" style={{ backgroundColor: GOLD }}>
                Placement
              </div>
              <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Tenant Placement</div>
              <div className="text-5xl font-bold" style={{ color: DARK }}>1 Month</div>
              <div className="text-sm text-gray-600 mt-1 mb-6">of monthly rent</div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Marketing, showings, background checks, credit reports, and lease preparation.
                Based on full monthly rent — not prorated.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 border border-[#e6dec7]">
              <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Onboarding</div>
              <div className="text-5xl font-bold" style={{ color: DARK }}>½ Month</div>
              <div className="text-sm text-gray-600 mt-1 mb-6">one-time</div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Property assessment and market prep. Based on full monthly rent, not prorated first month.
              </p>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-6">
            Final pricing varies by unit count and property type. Contact us for a custom proposal.
          </p>
        </div>
      </section>

      {/* ---------- GUARANTEES ---------- */}
      <section id="guarantees" className="py-24 bg-white scroll-mt-24" data-testid="pm-guarantees">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="In Writing"
            title="We put our commitment on paper."
            kicker="Not marketing lines — actual contract terms. The relationship should always be earned."
          />
          <div className="grid md:grid-cols-2 gap-6 mt-2">
            <div className="rounded-lg p-8 border-2" style={{ borderColor: GOLD, backgroundColor: '#faf8f4' }} data-testid="pm-guarantee-eviction">
              <ShieldCheck className="h-8 w-8 mb-4" style={{ color: GOLD }} strokeWidth={1.5} />
              <h3 className="text-2xl font-bold mb-3" style={{ color: DARK }}>
                Tenant Placement Eviction Guarantee
              </h3>
              <p className="text-gray-700 leading-relaxed">
                If we placed the tenant and have to evict, <strong>we do it at our own cost</strong>.
                Our screening standards should stand behind themselves.
              </p>
            </div>
            <div className="rounded-lg p-8 border-2" style={{ borderColor: GOLD, backgroundColor: '#faf8f4' }} data-testid="pm-guarantee-exit">
              <ArrowRight className="h-8 w-8 mb-4" style={{ color: GOLD }} strokeWidth={1.5} />
              <h3 className="text-2xl font-bold mb-3" style={{ color: DARK }}>
                30-Day Management Exit
              </h3>
              <p className="text-gray-700 leading-relaxed">
                If you're not satisfied, you can exit our management agreement with{' '}
                <strong>30 days' notice — no charge, no penalty</strong>. If we're not earning your business,
                you shouldn't be locked into it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- OWNER EXPERIENCE ---------- */}
      <section className="py-24" style={{ backgroundColor: '#f4efe6' }} data-testid="pm-experience">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Owner Experience"
            title="What it feels like to have Bespoke managing your property."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {experienceItems.map((it, i) => (
              <div key={it.headline} className="bg-white rounded-lg p-6 border border-[#e6dec7]" data-testid={`pm-exp-${i}`}>
                <div className="text-xs font-bold mb-2" style={{ color: GOLD }}>0{i + 1}</div>
                <h3 className="text-lg font-bold mb-2" style={{ color: DARK }}>{it.headline}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- BENJAMIN'S COMMITMENT ---------- */}
      <section className="py-24 bg-white" data-testid="pm-benjamin">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Eyebrow>Accountability</Eyebrow>
          <blockquote
            className="mt-6 text-2xl sm:text-3xl lg:text-4xl leading-snug font-medium italic"
            style={{ color: DARK }}
          >
            &ldquo;I literally make all decisions regarding a property as though it is my money being
            spent — and with the preservation of the investment in mind.&rdquo;
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="w-10 h-px" style={{ background: GOLD }} />
            <div>
              <div className="font-bold" style={{ color: DARK }}>Benjamin Wright</div>
              <div className="text-xs tracking-widest uppercase text-gray-500 mt-0.5">
                Principal Broker / Owner
              </div>
            </div>
            <div className="w-10 h-px" style={{ background: GOLD }} />
          </div>
        </div>
      </section>

      {/* ---------- FINAL CTA + LEAD FORM ---------- */}
      <section
        id="owner-inquiry"
        className="py-24 scroll-mt-24"
        style={{ backgroundColor: DARK }}
        data-testid="pm-final-cta"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Eyebrow>Let's Talk</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight">
              Let's talk about your property.
            </h2>
            <p className="mt-5 text-lg text-white/80 leading-relaxed max-w-lg">
              Whether you're already managing a rental, preparing to rent a property you own, or considering
              adding an investment property to your portfolio — let's look at the property, the market,
              and the opportunities together.
            </p>
            <div className="mt-8 space-y-4">
              {[
                'Free consultation with a licensed property manager',
                'Written rent recommendation for your property',
                'Zero obligation — bring the address, we bring the numbers',
              ].map((it) => (
                <div key={it} className="flex items-start gap-3 text-white/85">
                  <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
                  <span>{it}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="tel:9013904414"
                className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 px-5 py-3 rounded-md font-semibold transition-colors"
                data-testid="pm-cta-call"
              >
                Call 901-390-4414
              </a>
              <button
                type="button"
                onClick={() => setDownloadOpen(true)}
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white/10 px-5 py-3 rounded-md font-semibold transition-colors"
                data-testid="pm-cta-download-alt"
              >
                <Download className="h-4 w-4" /> Or download the Summary first
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-2xl">
            <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${GOLD}20`, color: GOLD }}>
              Free · No Obligation
            </div>
            <h3 className="text-2xl font-bold mb-2" data-testid="owner-inquiry-heading" style={{ color: DARK }}>
              Request a Management Consultation
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Tell us about your property and we'll send back a rent recommendation, marketing timeline,
              and projected annual return within 48 hours.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required data-testid="pm-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required data-testid="pm-email-input" />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required data-testid="pm-phone-input" />
                <SmsOptInCheckbox
                  checked={formData.sms_opt_in}
                  onChange={(v) => setFormData({ ...formData, sms_opt_in: v })}
                />
              </div>
              <div>
                <Label htmlFor="property_address">Property Address</Label>
                <Input id="property_address" name="property_address" value={formData.property_address} onChange={handleInputChange} data-testid="pm-address-input" />
              </div>
              <div>
                <Label htmlFor="message">Tell us about your property</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Number of units, current status, any specific concerns..."
                  data-testid="pm-message-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full text-white"
                style={{ backgroundColor: GOLD }}
                data-testid="pm-submit-btn"
              >
                Send My Request
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertyManagement;
